import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { combineLatest, Observable } from 'rxjs';
import { map, publishReplay, refCount} from 'rxjs/operators';
import { AppConfig } from "app/app.config";
import { profileType } from './auth.service';
import {AccountantClient, DownloadableFile} from "app/modules/document/accountant-document/accountant-document.fn";

export type apiDocumentType = {
  Folder: string;
  FileName: string;
  Extension: string;
  LastWriteTime: null | string;
  CreationTime: string;
  Length: number;
  FileViewTime: null | string;

  DatePostedFull: string;
  DatePostedLocal: string;
  Type: string;
  FileNameLower: string;
  Tags: string[];
};

type internalDocumentType = {
  Folder: string;
  FileName: string;
  Extension: string;
  LastWriteTime: null | string;
  CreationTime: string;
  Length: number;
  FileViewTime: null | string;
  DatePostedFull: string;
  Tags: string[];
};

export type portfolioApiDataType = {
  blendedAnnualized: number;
  fxRate: {
    RateDate: string;
    UsdToCadRate: number;
  };
  inceptionDate: string;
  otherHoldings: number;
  total: number;
  totalWithOtherHoldings: number;
  ytdReturn: number;
  CadblendedAnnualized: number;
  CadytdReturn: number;
};

export type pgfActivityType = {
  ActivityId: number;
  ActivityType: 'Grant' | 'Donation';
  ActivityDate: string;
  ActivityStatus: string;
  FundName: string;
  CharityId: number;
  CharityName: string;
  DonorName: string;
  CharityType: string;
  AmountCAD: number;
  AmountUSD: number;
  SpecialMessage: string;
  Designation: string;
  TransactionCurrency: string;
};

export enum API_TYPE {
  'AnnualizedReturns' = 'AnnualizedReturns',

  'CalenderYearReturns' = 'CalenderYearReturns',
  'CalenderYearReturnsDetails' = 'CalenderYearReturnsDetails',
  'CashFlowAnnual' = 'CashFlowAnnual',
  'CashFlowRollingDetails' = 'CashFlowRollingDetails',
  'ContributionsWithdrawals' = 'ContributionsWithdrawals',
  'Plans' = 'Plans',
  'Portfolio' = 'Portfolio',
  'PortfolioAssets' = 'PortfolioAssets',
  'PortfolioTotals' = 'PortfolioTotals',
  'TotalAmountInvested' = 'TotalAmountInvested',
  'TotalAmountInvestedDetail' = 'TotalAmountInvestedDetail',
}

@Injectable()

export class APIService {
  private http = inject(HttpClient);
  private config = inject(AppConfig);

  public loadedDocs: Record<string, Observable<apiDocumentType[]>> = {};
  public profiles: Record<string, Observable<[profileType[],profileType[]]>> = {};
  public pgfActivity: Record<string, Observable<pgfActivityType[]>> = {};

  get<T>(url: string) {
    return this.http.get<T>(url);
  }

  getBlob(params: Record<string, string>) {
    return this.http.get(this.config.resources.downloadfileUrl + '?path=' + encodeURIComponent(params['path']), { responseType: 'blob' });
  }

  post<T>(api: string, formData: FormData | Record<string, unknown> = {}, options = {}) {
    return this.http.post<T>(api, formData, options);
  }

  postMultipart<T>(api: string, formData: Record<string, unknown> = {}) {
    return this.http.post<T>(api, formData, {headers: new HttpHeaders({
      "content-type": "application/json",
      "enctype": "multipart/form-data",
      "Content-Disposition": "form-data"
      })});
  }

  getDocumentsList(reRgId: number) {
    // for more info see #3 here https://indepth.dev/posts/1248/fastest-way-to-cache-for-lazy-developers-angular-with-rxjs
    // Cache it once
    if (!this.loadedDocs[reRgId]) {
      this.loadedDocs[reRgId] = this.post<internalDocumentType[]>(
        this.config.document.pagedDocumentUrl,
        {reRgId},
        {
          headers: new HttpHeaders({
            "content-type": "application/json",
            "enctype": "multipart/form-data"
          })
        }
      ).pipe(
        map(res => {
          return res.map<apiDocumentType>(r => {
            const datePostedFull = r.LastWriteTime && r.LastWriteTime > r.CreationTime ? r.LastWriteTime : r.CreationTime;
            const dp = new Date(datePostedFull);
            const lname = r.FileName.toLowerCase();
            return {
              Folder: r.Folder,
              FileName: r.FileName,
              Extension: r.Extension,
              LastWriteTime: r.LastWriteTime,
              CreationTime: r.CreationTime,
              Length: r.Length,
              FileViewTime: r.FileViewTime || null,
              DatePostedFull: datePostedFull,
              DatePostedLocal: `${dp.getFullYear()}-${String(dp.getMonth() + 1).padStart(2, '0')}-${String(dp.getDate()).padStart(2, '0')}`,
              Type: r.Extension.substr(1),
              FileNameLower: lname,
              Tags: lname.split(/[^a-z0-9]+/).filter((v,i,a)=>v!=='' && a.indexOf(v) === i)
            };
          });
        }),
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount(), // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      );
    }
    return this.loadedDocs[reRgId];
  }

  getProfileByReRgId(reRgId: number) {
    if (!this.profiles[reRgId]) {
      this.profiles[reRgId] = combineLatest([
        this.get<profileType[]>(this.config.profile.getReRgsRelated+'/'+reRgId),
        this.get<profileType[]>(this.config.profile.getReRgsAdvisors+'/'+reRgId)
      ]).pipe(
        publishReplay(1),
        refCount(),
      );
    }
    return this.profiles[reRgId];
  }

  getPgfActivityByReRgId(reRgId: number) {
    if (!this.pgfActivity[reRgId]) {
      this.pgfActivity[reRgId] = this.get<pgfActivityType[]>(this.config.pgf.getDisbursementDonationUrl+'/'+reRgId).pipe(
        publishReplay(1),
        refCount(),
      );
    }
    return this.pgfActivity[reRgId];
  }

  sendMailWithAttachments(formData: FormData): Observable<string> {
    this.clearDocumentsCache();
    return this.post(this.config.advisor.sendMailWithAttachment, formData);
  }

  clearDocumentsCache() {
    this.loadedDocs = {};
  }

  public formatCurrency(n: undefined | null | string | number) {
    if (n === null || n === undefined || n === '') return '';
    const t = n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return t.startsWith('-') ? '(' + t.substring(1) + ')' : t;
  }

  getPlanPath(planId: string, isPershing: boolean) {
    const plId = isPershing && planId === "" ? "USD" : planId;
    return ['CAD','USD'].includes(plId) ? `0/${plId}` : plId;
  }

  getAccountantClients(): Observable<AccountantClient[]> {
    return this.get<AccountantClient[]>(this.config.accountantDocuments.clientsUrl);
  }

  getAccountantClientDocuments(clientId: string): Observable<DownloadableFile[]> {
    return this.get<DownloadableFile[]>(this.config.accountantDocuments.clientDocumentsUrl + '?clientId=' + clientId);
  }

  download(downloadToken: string) {
    return this.http.get(this.config.accountantDocuments.downloadUrl + '?downloadToken=' + encodeURIComponent(downloadToken), {responseType: 'blob', observe: 'response'});
  }

  downloadBulk(downloadTokens: string[]) {
    return this.http.post(this.config.accountantDocuments.bulkDownloadUrl, { downloadTokens }, {responseType: 'blob', observe: 'response'});
  }
}
